import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { format, formatDistanceToNow } from "date-fns";
import {
  ArrowLeft, Trophy, Target, Star, Flame, BookOpen, Zap, FileText, Calculator, Atom, PenTool, Brain, AlertTriangle,
  Medal, AlertOctagon, ChevronRight,
} from "lucide-react";
import { ParentLayout } from "@/components/parent/ParentLayout";

interface ChildDetail {
  child: {
    id: string;
    firstName: string;
    lastName: string;
    grade: number;
    school: string | null;
    profilePhotoUrl: string | null;
    subscriptionStatus: string;
    subscriptionPlan: string | null;
    subscriptionEndDate: string | null;
    lastActiveAt: string | null;
    createdAt: string;
  };
  stats: {
    totalPoints: number;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
    currentLevel: number;
    totalXP: number;
    nextLevelXP: number;
    testsCompleted: number;
  };
  subjectBreakdown: Array<{
    subject: string;
    totalPoints: number;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
    currentStreak: number;
    bestStreak: number;
  }>;
  streaks: Array<{
    subject: string;
    topic: string;
    currentStreak: number;
    bestStreak: number;
    masteryLevel: number;
    lastAttemptAt: string;
  }>;
  recentActivity: Array<{
    date: string;
    questionsAttempted: number;
    questionsCorrect: number;
  }>;
}

const formatSubject = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

const subjectIcon = (s: string) => {
  const l = s.toLowerCase();
  if (l.includes("math")) return Calculator;
  if (l.includes("science")) return Atom;
  if (l.includes("english") || l.includes("language")) return PenTool;
  if (l.includes("reading")) return BookOpen;
  return Brain;
};

export default function ParentChildDetail() {
  const { childId } = useParams<{ childId: string }>();
  const [openSubject, setOpenSubject] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<ChildDetail>({
    queryKey: [`/api/parent/children/${childId}`],
    enabled: !!childId,
  });

  const { data: rank } = useQuery<{ rank: number | null; totalXP: number; totalStudents: number; weeklyRank: number | null; weeklyPoints: number }>({
    queryKey: [`/api/parent/children/${childId}/leaderboard-rank`],
    enabled: !!childId,
  });

  const { data: weak } = useQuery<{ weakTopics: Array<{ subject: string; topic: string; mastery: number }> }>({
    queryKey: [`/api/parent/children/${childId}/weak-topics`],
    enabled: !!childId,
  });

  if (isLoading) {
    return (
      <ParentLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[0,1,2,3,4].map((i) => <Skeleton key={i} className="h-20" />)}
          </div>
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </ParentLayout>
    );
  }

  if (isError || !data) {
    return (
      <ParentLayout>
        <Card className="max-w-md mx-auto">
          <CardContent className="py-10 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
            <p className="font-semibold">Unable to load details</p>
            <Button asChild variant="outline">
              <Link href="/parent">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to overview
              </Link>
            </Button>
          </CardContent>
        </Card>
      </ParentLayout>
    );
  }

  const { child, stats, subjectBreakdown, streaks, recentActivity } = data;
  const xpPct = stats.nextLevelXP > 0 ? Math.min(Math.round((stats.totalXP / stats.nextLevelXP) * 100), 100) : 100;
  const topStreaks = [...streaks].sort((a, b) => b.masteryLevel - a.masteryLevel).slice(0, 6);
  const last7 = recentActivity.slice(-7);

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Button asChild variant="ghost" size="icon">
            <Link href="/parent">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <Avatar className="h-12 w-12">
            <AvatarImage src={child.profilePhotoUrl || undefined} />
            <AvatarFallback>{child.firstName.charAt(0)}{child.lastName.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold">{child.firstName} {child.lastName}</h1>
            <p className="text-sm text-muted-foreground">
              Grade {child.grade}
              {child.school ? ` · ${child.school}` : ""}
              {child.lastActiveAt ? ` · Active ${formatDistanceToNow(new Date(child.lastActiveAt), { addSuffix: true })}` : ""}
            </p>
          </div>
          <Badge variant={child.subscriptionStatus === "active" ? "default" : "outline"}>
            {child.subscriptionStatus}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Star} label="Level" value={stats.currentLevel} />
          <StatCard icon={Zap} label="Total XP" value={stats.totalXP.toLocaleString()} />
          <StatCard icon={Target} label="Questions" value={stats.totalQuestions.toLocaleString()} />
          <StatCard icon={Trophy} label="Accuracy" value={`${stats.accuracy}%`} />
          <StatCard icon={FileText} label="Tests" value={stats.testsCompleted} />
        </div>

        {rank && rank.rank !== null && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="rounded-md bg-muted p-2.5">
                  <Medal className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">All-time rank · Grade {child.grade}</p>
                  <p className="text-lg font-bold">#{rank.rank} <span className="text-sm font-normal text-muted-foreground">of {rank.totalStudents}</span></p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="rounded-md bg-muted p-2.5">
                  <Trophy className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">This week's rank · Grade {child.grade}</p>
                  <p className="text-lg font-bold">{rank.weeklyRank ? `#${rank.weeklyRank}` : "–"} <span className="text-sm font-normal text-muted-foreground">{rank.weeklyPoints > 0 ? `${rank.weeklyPoints} pts this week` : "no points yet"}</span></p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm mb-2 flex-wrap gap-1">
              <span className="font-medium">XP progress to next level</span>
              <span className="text-muted-foreground">{stats.totalXP.toLocaleString()} / {stats.nextLevelXP.toLocaleString()} XP</span>
            </div>
            <Progress value={xpPct} className="h-2" />
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Subject breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {subjectBreakdown.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No subject activity yet.</p>
              ) : (
                <div className="space-y-1">
                  {subjectBreakdown.map((s) => {
                    const Icon = subjectIcon(s.subject);
                    return (
                      <button
                        key={s.subject}
                        type="button"
                        onClick={() => setOpenSubject(s.subject)}
                        className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-muted/50 transition-colors text-left"
                        data-testid={`subject-row-${s.subject}`}
                      >
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-sm gap-2">
                            <span className="truncate font-medium">{formatSubject(s.subject)}</span>
                            <span className="text-muted-foreground">{s.accuracy}%</span>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{s.correctAnswers}/{s.totalQuestions} correct</span>
                            <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{s.currentStreak}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top topics</CardTitle>
            </CardHeader>
            <CardContent>
              {topStreaks.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No topic activity yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {topStreaks.map((s) => (
                    <li key={`${s.subject}-${s.topic}`} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{s.topic}</p>
                        <p className="text-xs text-muted-foreground truncate">{formatSubject(s.subject)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold">{s.masteryLevel}%</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-end gap-1"><Flame className="w-3 h-3" />{s.currentStreak}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Last 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            {last7.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No activity in the past week.</p>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {last7.map((d) => {
                  const pct = d.questionsAttempted > 0 ? Math.round((d.questionsCorrect / d.questionsAttempted) * 100) : 0;
                  return (
                    <div key={d.date} className="text-center">
                      <p className="text-xs text-muted-foreground">{format(new Date(d.date), "EEE")}</p>
                      <p className="font-semibold text-sm">{d.questionsAttempted}</p>
                      <p className="text-xs text-muted-foreground">{pct}%</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {weak && weak.weakTopics.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-amber-600" />
                Needs more practice
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {weak.weakTopics.map((t, i) => (
                  <li key={`${t.subject}-${t.topic}-${i}`} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{t.topic}</p>
                      <p className="text-xs text-muted-foreground truncate">{formatSubject(t.subject)}</p>
                    </div>
                    <span className="text-sm font-semibold text-amber-600 shrink-0">{t.mastery}% mastery</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-3">Topics where mastery is under 60%. Encourage {child.firstName} to retry these in the app.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <SubjectDeepDiveSheet
        childId={childId!}
        childName={child.firstName}
        subject={openSubject}
        onClose={() => setOpenSubject(null)}
      />
    </ParentLayout>
  );
}

interface SubjectDeep {
  subject: string;
  displayName: string;
  stat: null | {
    totalPoints: number;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
    currentStreak: number;
    bestStreak: number;
  };
  topics: Array<{
    topic: string;
    mastery: number;
    currentStreak: number;
    bestStreak: number;
    lastAttemptAt: string | null;
  }>;
  recent: Array<{ date: string; attempts: number; correct: number }>;
}

function SubjectDeepDiveSheet({ childId, childName, subject, onClose }: {
  childId: string;
  childName: string;
  subject: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery<SubjectDeep>({
    queryKey: subject ? [`/api/parent/children/${childId}/subjects/${encodeURIComponent(subject)}`] : ["subject-deep-disabled"],
    enabled: !!subject,
  });

  return (
    <Sheet open={!!subject} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{data?.displayName ?? subject ?? "Subject"}</SheetTitle>
          <SheetDescription>{childName}'s detail</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20" />
              <Skeleton className="h-40" />
            </div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : (
            <>
              {data.stat && (
                <Card>
                  <CardContent className="p-4 grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Accuracy</p>
                      <p className="text-lg font-bold">{data.stat.accuracy}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Questions</p>
                      <p className="text-lg font-bold">{data.stat.totalQuestions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Streak</p>
                      <p className="text-lg font-bold flex items-center justify-center gap-1"><Flame className="w-4 h-4 text-amber-600" />{data.stat.currentStreak}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Topics in this subject</p>
                {data.topics.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No topic activity yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.topics.map((t) => (
                      <li key={t.topic} className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{t.topic}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.lastAttemptAt ? `Last ${formatDistanceToNow(new Date(t.lastAttemptAt), { addSuffix: true })}` : "Not attempted"}
                          </p>
                        </div>
                        <span className={`text-sm font-semibold shrink-0 ${t.mastery >= 80 ? "text-green-600" : t.mastery >= 60 ? "text-foreground" : "text-amber-600"}`}>
                          {t.mastery}%
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {data.recent.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Last 30 days of activity</p>
                  <div className="flex items-end gap-1 h-20">
                    {data.recent.slice(-30).map((d) => {
                      const max = Math.max(...data.recent.map((r) => r.attempts), 1);
                      const h = Math.max((d.attempts / max) * 100, 4);
                      return (
                        <div
                          key={d.date}
                          className="flex-1 bg-primary/70 rounded-sm"
                          style={{ height: `${h}%` }}
                          title={`${d.date}: ${d.attempts} questions, ${d.correct} correct`}
                        />
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{data.recent.reduce((acc, r) => acc + r.attempts, 0)} attempts · {data.recent.reduce((acc, r) => acc + r.correct, 0)} correct in the last 30 days</p>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <Icon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
        <p className="text-xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
